// src/components/resources/tools/generators/ColorPalette/palette-build.ts
//
// Pure palette-generation core harvested from ColorPaletteGenerator.tsx
// (A11/C1). Given the generator's form values it produces the full ColorInfo[]
// (harmony colors + optional variations/neutrals, sorted + named + analyzed)
// and the palette analysis. All of the component's setState bookkeeping stays
// in the orchestrator; this is the testable, framework-free transformation —
// the highest-risk path now has direct coverage independent of React.
import {
  generateHarmonyColors,
  getContrastRatio,
  getLuminance,
  isDark,
  getColorTemperature,
  getWcagLevel,
  parseColorString,
  type HarmonyType,
} from '~/lib/utils/colors'
import { rgbToHsl, hslToRgb, rgbToHex, lighten, darken } from '~/lib/utils/colors/transforms'
import { getString, getBoolean } from '~/lib/utils/form-values'
import { analyzeColorInfoPalette } from './palette-analysis'
import type { ColorAnalysis, ColorInfo, PaletteWcagLevel } from './types'

/**
 * Maps {@link getWcagLevel} uppercase labels to the palette's lowercase convention.
 *
 * @param level - WCAG level from the colors utility (`'AAA' | 'AA' | 'FAIL'`).
 * @returns Lowercase palette label (`'aaa' | 'aa' | 'fail'`).
 */
export function toPaletteWcag(level: ReturnType<typeof getWcagLevel>): PaletteWcagLevel {
  switch (level) {
    case 'AAA':
      return 'aaa'
    case 'AA':
      return 'aa'
    case 'FAIL':
      return 'fail'
    default: {
      const _exhaustive: never = level
      return _exhaustive
    }
  }
}

/** A built palette: the colors plus their analysis. */
export interface BuiltPalette {
  colors: ColorInfo[]
  analysis: ColorAnalysis
}

/**
 * Build a palette from the generator's form values.
 *
 * Reads `baseColor` / `harmonyType` / `paletteSize` / `includeNeutrals` /
 * `includeVariations` / `saturation` / `lightness` from `formData` (with the
 * same fallbacks the component has always used), generates the harmony colors,
 * appends optional variations/neutrals, sorts and names them, computes per-color
 * analysis, and runs the palette-level analysis.
 *
 * @param formData - The generator's form state
 * @returns The built `colors` and their `analysis`
 * @throws If `baseColor` is unparseable, or a generated color fails to parse.
 */
export function buildPalette(formData: Record<string, unknown>): BuiltPalette {
  const baseColor = getString(formData.baseColor, '#3B82F6')
  const harmonyType = getString(formData.harmonyType, 'monochromatic') as HarmonyType
  const paletteSize = parseInt(getString(formData.paletteSize, '5'), 10)
  const includeNeutrals = getBoolean(formData.includeNeutrals)
  const includeVariations = getBoolean(formData.includeVariations)
  const saturationPref = getString(formData.saturation, 'original')
  const lightnessPref = getString(formData.lightness, 'original')

  // Validate base color
  const parsedColor = parseColorString(baseColor)
  if (!parsedColor) throw new Error('Invalid base color provided')

  // Generate exactly the requested number of colors
  const mainPalette = generateHarmonyColors(
    baseColor,
    harmonyType,
    paletteSize, // Generate exactly the requested number of colors
    saturationPref,
    lightnessPref
  )

  // The function already returns exactly the requested number of colors
  const finalPalette = mainPalette

  // Add variations if requested (these are additional colors, not part of the main palette)
  let variationColors: string[] = []
  if (includeVariations) {
    const baseRgb = parseColorString(baseColor)
    if (baseRgb) {
      const baseHsl = rgbToHsl(baseRgb)
      // Create lighter and darker variations
      const lighterHsl = lighten(baseHsl, 0.2)
      const darkerHsl = darken(baseHsl, 0.2)
      variationColors = [
        rgbToHex(hslToRgb(lighterHsl)),
        rgbToHex(hslToRgb(darkerHsl))
      ]
    }
  }

  // Add neutral colors if requested (these are additional colors, not part of the main palette)
  let neutralColors: string[] = []
  if (includeNeutrals) {
    // Generate neutral grays with slight variations
    neutralColors = [
      '#808080', // Medium gray
      '#606060', // Dark gray
      '#A0A0A0'  // Light gray
    ]
  }

  // Combine all colors, tagging each with its role name BEFORE the sort below.
  // The sort reorders colors for presentation, so deriving the name from the
  // post-sort index (the previous behaviour) mislabeled them: a neutral gray
  // that sorted to the front became "Primary", and harmony colors that sorted
  // into the tail became "Neutral". Tie each name to its ORIGINAL group here and
  // carry it through the sort so the label always matches what the color IS.
  const namedColors: Array<{ color: string; name: string }> = [
    ...finalPalette.map((color, i) => ({
      color,
      name: i === 0 ? 'Primary' : `Harmony ${i}`,
    })),
    ...variationColors.map((color, i) => ({ color, name: `Variation ${i + 1}` })),
    ...neutralColors.map((color, i) => ({ color, name: `Neutral ${i + 1}` })),
  ].filter(
    (entry): entry is { color: string; name: string } =>
      typeof entry.color === 'string' && entry.color.length > 0,
  )

  // Sort colors by hue first, then saturation, then lightness for better
  // presentation, carrying each color's role name through the reorder.
  const sortedColors = namedColors.sort((a, b) => {
    const rgbA = parseColorString(a.color)
    const rgbB = parseColorString(b.color)
    if (!rgbA || !rgbB) return 0

    const hslA = rgbToHsl(rgbA)
    const hslB = rgbToHsl(rgbB)

    // Sort by hue first (most important for visual organization)
    const hueDiff = hslA[0] - hslB[0]
    if (Math.abs(hueDiff) > 5) {
      return hueDiff
    }

    // Then by saturation (higher saturation first)
    const satDiff = hslB[1] - hslA[1]
    if (Math.abs(satDiff) > 10) {
      return satDiff
    }

    // Finally by lightness (lighter first)
    return hslA[2] - hslB[2]
  })

  // Convert to ColorInfo objects, keeping the role name assigned above.
  const colors: ColorInfo[] = sortedColors.map(({ color, name }) => {
    const rgb = parseColorString(color)
    if (!rgb) throw new Error(`Failed to parse color: ${color}`)

    const hsl = rgbToHsl(rgb)
    const whiteContrast = getContrastRatio(rgb, [255, 255, 255])
    const blackContrast = getContrastRatio(rgb, [0, 0, 0])
    const bestContrast = Math.max(whiteContrast, blackContrast)
    const wcagLevel = toPaletteWcag(getWcagLevel(bestContrast))
    const temperature = getColorTemperature(rgb)
    const luminance = getLuminance(rgb)

    return {
      color,
      name,
      rgb,
      hsl,
      hex: color,
      contrast: bestContrast,
      wcag: wcagLevel,
      temperature,
      isDark: isDark(rgb),
      luminance,
      metadata: {
        dominance: rgb[0] > rgb[1] && rgb[0] > rgb[2] ? 'red' :
                  rgb[1] > rgb[0] && rgb[1] > rgb[2] ? 'green' :
                  rgb[2] > rgb[0] && rgb[2] > rgb[1] ? 'blue' : 'balanced',
        saturationLevel: hsl[1] < 10 ? 'grayscale' :
                        hsl[1] < 30 ? 'muted' :
                        hsl[1] < 60 ? 'moderate' :
                        hsl[1] < 80 ? 'vibrant' : 'intense',
        brightnessLevel: hsl[2] < 20 ? 'very-dark' :
                        hsl[2] < 40 ? 'dark' :
                        hsl[2] < 60 ? 'medium' :
                        hsl[2] < 80 ? 'light' : 'very-light',
        useCases: temperature === 'warm' ? ['Call-to-action', 'Highlights', 'Accents'] :
                 temperature === 'cool' ? ['Backgrounds', 'Text', 'Navigation'] :
                 ['Neutral elements', 'Borders', 'Shadows'],
        emotions: temperature === 'warm' ? ['Energy', 'Excitement', 'Warmth'] :
                 temperature === 'cool' ? ['Calm', 'Trust', 'Professional'] :
                 ['Balance', 'Stability', 'Neutrality']
      }
    }
  })

  // Analyze the generated palette
  const analysis = analyzeColorInfoPalette(colors)

  return { colors, analysis }
}
